import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
} from '@mui/icons-material';

const SchedulerToggleModal = ({
  open,
  onClose,
  onConfirm,
  isEnabled,
  loading = false,
}) => {
  // isEnabled = current state, so if it's enabled, we're stopping it
  const isStopping = isEnabled;
  const isStarting = !isEnabled;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {isStopping ? (
            <StopIcon color="warning" />
          ) : (
            <PlayArrowIcon color="success" />
          )}
          <Typography variant="h6">
            {isStopping ? 'Turn Off Scheduler' : 'Turn On Scheduler'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Alert 
            severity={isStopping ? 'warning' : 'info'} 
            icon={isStopping ? <StopIcon /> : <PlayArrowIcon />}
          >
            <Typography variant="body2">
              {isStopping
                ? 'Are you sure you want to turn off the scheduler? This will disable automatic daily report generation and email sending.'
                : 'Are you sure you want to turn on the scheduler? This will enable automatic daily report generation and email sending according to the configured schedule.'}
            </Typography>
          </Alert>
        </Box>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            <strong>Current Status:</strong> {isEnabled ? 'Scheduler is running' : 'Scheduler is stopped'}
          </Typography>
        </Box>

        {isStopping && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              <strong>What will happen:</strong>
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Automatic daily report generation will stop</li>
              <li>Email notifications will not be sent automatically</li>
              <li>You can still generate reports manually</li>
            </ul>
          </Box>
        )}

        {isStarting && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              <strong>What will happen:</strong>
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Daily reports will be generated automatically</li>
              <li>Email notifications will be sent to configured recipients</li>
              <li>Reports will be archived according to the schedule</li>
            </ul>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={isStopping ? 'warning' : 'success'}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading 
            ? (isStopping ? 'Turning Off...' : 'Turning On...') 
            : (isStopping ? 'Turn Off Scheduler' : 'Turn On Scheduler')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SchedulerToggleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isEnabled: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
};

export default SchedulerToggleModal;
